import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import ArrowLeft from 'lucide-react-native/icons/arrow-left';
import Calendar from 'lucide-react-native/icons/calendar';
import Clock from 'lucide-react-native/icons/clock';
import Heart from 'lucide-react-native/icons/heart';
import MapPin from 'lucide-react-native/icons/map-pin';
import Ticket from 'lucide-react-native/icons/ticket';
import { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { CityCrossSell } from '@/components/detail/city-cross-sell';
import { EventHead } from '@/components/detail/event-head';
import { EventRedirect } from '@/components/detail/event-redirect';
import { EventHotels } from '@/components/detail/hotels-nearby';
import { RelatedList } from '@/components/detail/related-list';
import { ShowDates } from '@/components/detail/show-dates';
import { EventCard } from '@/components/riviera/event-card';
import { NoImagePlaceholder } from '@/components/riviera/no-image-placeholder';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { Badge, BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Fonts, MaxContentWidth, Radius, Spacing } from '@/constants/theme';
import { useBreakpoint } from '@/hooks/use-breakpoint';
import { useEventShow } from '@/hooks/use-event';
import { useRelatedEvents } from '@/hooks/use-related';
import { useTheme } from '@/hooks/use-theme';
import { isEventPast } from '@todo/core/date-range';
import { cities } from '@/lib/events-data';
import { localizedDescription, localizedTitle } from '@todo/core/i18n/event-content';
import { usableImageUrl } from '@todo/core/image-url';
import { currencySymbol, formatDateLong, formatTimeRange } from '@todo/core/i18n/format';
import { useLocale } from '@todo/core/i18n/use-locale';
import { openExternal } from '@todo/core/open-external';
import { type DbEvent } from '@todo/core/supabase';

export { generateEventStaticParams as generateStaticParams } from '@/lib/detail/event-detail';

const tagVariant: Record<string, BadgeVariant> = {
  concerts: 'destructive',
  exhibitions: 'primary',
  parties: 'chart4',
  theatre: 'gold',
  gastronomy: 'chart4',
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

function formatPrice(min: number | null, currency: string | null, t: Translate) {
  if (min == null) return t('events.free');
  const symbol = currencySymbol(currency);
  return t('common.fromPrice', { price: `${symbol}${min}` });
}

// Hero image with fallback. A missing OR broken image_url (many AWIN/Fnac URLs
// 404) shows the on-brand placeholder. Kept as its own component so the failure
// state resets per event — the screen itself is reused across navigations (see
// the EventHead `key` note), so screen-level state would go stale.
function HeroImage({ event, theme }: { event: DbEvent; theme: ReturnType<typeof useTheme> }) {
  const [failed, setFailed] = useState(false);
  const imageUrl = usableImageUrl(event.image_url);
  if (!imageUrl || failed) {
    return (
      <View style={styles.hero}>
        <NoImagePlaceholder size={120} />
      </View>
    );
  }
  return (
    // AWIN/Fnac feed images are tiny thumbnails (~222x222) with no larger
    // variant available from the source — stretching them full-bleed with
    // `cover` looks blurry/blocky, so letterbox them with `contain` on a muted
    // backdrop instead.
    <Image
      source={{ uri: imageUrl }}
      style={[styles.hero, event.source === 'awin_fnac' && { backgroundColor: theme.muted }]}
      contentFit={event.source === 'awin_fnac' ? 'contain' : 'cover'}
      onError={() => setFailed(true)}
    />
  );
}

// Shown in place of the ticket Button for events past their 30-day-retention
// window's start (see isEventPast) — the vendor ticket URL is dead by then, so
// this avoids sending a tap to a broken affiliate link and funnels toward the
// "more upcoming events" list below instead.
function EndedNotice({ theme, t }: { theme: ReturnType<typeof useTheme>; t: Translate }) {
  return (
    <View style={[styles.endedNotice, { backgroundColor: theme.muted }]}>
      <Text style={[styles.endedBanner, { color: theme.text }]}>{t('events.endedBanner')}</Text>
      <Text style={[styles.endedHint, { color: theme.mutedForeground }]}>
        {t('events.endedHint')}
      </Text>
    </View>
  );
}

export default function EventScreen() {
  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { isLg } = useBreakpoint();
  const { id } = useLocalSearchParams<{ id: string }>();
  // `id` is a show_slug — or an old per-date row id, which redirects. Until the
  // client fetch lands this is the record the static export baked in.
  const { show, redirectPath, loading, error } = useEventShow(id);
  const [saved, setSaved] = useState(false);

  const event = show?.event ?? null;
  const dates = show?.dates ?? [];

  const cityName = event?.city_id
    ? (cities.find((c) => c.id === event.city_id)?.name ?? event.city_id)
    : null;
  const location = event
    ? [event.venue_name, cityName].filter(Boolean).join(', ')
    : '';
  const title = event ? localizedTitle(event, locale) : '';
  const descriptionText = event ? localizedDescription(event, locale) : null;
  const isPast = event ? isEventPast(event) : false;

  const { related, relatedSeedKey, seeAllHref, cityId } = useRelatedEvents(event);
  const relatedHeading = cityId
    ? t('events.moreUpcomingCity', { city: cityName })
    : t('events.moreUpcoming');

  if (redirectPath) return <EventRedirect id={id} path={redirectPath} />;

  return (
    <View style={[styles.root, { backgroundColor: theme.background }]}>
      <Stack.Screen options={{ headerShown: false }} />
      {show ? (
        <EventHead
          show={show}
          locale={locale}
          related={related}
          relatedSeedKey={relatedSeedKey}
        />
      ) : null}
      <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
        <SiteHeader />
      </SafeAreaView>
      <ScrollView
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}>
        {event ? (
          <>
          <View style={styles.centered}>
            <View style={[styles.page, isLg && styles.pageLg]}>
              <View style={[styles.heroBox, isLg && styles.heroBoxLg]}>
                <HeroImage key={event.show_slug} event={event} theme={theme} />
                <LinearGradient
                  colors={['rgba(0,0,0,0.45)', 'rgba(0,0,0,0)']}
                  style={styles.heroScrim}
                />
                <Pressable
                  accessibilityLabel={t('a11y.back')}
                  onPress={() => router.back()}
                  style={({ pressed }) => [styles.backBtn, pressed && { opacity: 0.7 }]}>
                  <ArrowLeft size={20} color="#fff" />
                </Pressable>
                <View style={styles.heroBadge}>
                  <Badge variant={tagVariant[event.category_id ?? ''] ?? 'primary'}>
                    {event.category_id
                      ? t(`categoryLabels.${event.category_id}`, {
                          defaultValue: t('categoryLabels.fallback'),
                        })
                      : t('categoryLabels.fallback')}
                  </Badge>
                </View>
                <Pressable
                  accessibilityLabel={saved ? t('a11y.removeFavorite') : t('a11y.addFavorite')}
                  onPress={() => setSaved((v) => !v)}
                  style={({ pressed }) => [
                    styles.heartBtn,
                    { backgroundColor: theme.card },
                    pressed && { opacity: 0.7 },
                  ]}>
                  <Heart
                    size={18}
                    color={saved ? theme.destructive : theme.text}
                    fill={saved ? theme.destructive : 'transparent'}
                  />
                </Pressable>
              </View>

              <View
                style={[
                  styles.card,
                  isLg && styles.cardLg,
                  { backgroundColor: theme.card, borderColor: theme.border },
                ]}>
                <Text
                  style={[
                    styles.title,
                    isLg && styles.titleLg,
                    { color: theme.text, fontFamily: Fonts?.serif },
                  ]}>
                  {title}
                </Text>

                <View style={styles.metaList}>
                  <View style={styles.metaRow}>
                    <Calendar size={16} color={theme.gold} />
                    <Text style={[styles.metaText, { color: theme.text }]}>
                      {formatDateLong(event.start_ts, event.tz, locale)}
                      {dates.length > 1
                        ? ` · ${t('events.moreDates', { count: dates.length - 1 })}`
                        : ''}
                    </Text>
                  </View>
                  <View style={styles.metaRow}>
                    <Clock size={16} color={theme.gold} />
                    <Text style={[styles.metaText, { color: theme.text }]}>
                      {formatTimeRange(event.start_ts, event.end_ts, event.tz, locale)}
                    </Text>
                  </View>
                  {location ? (
                    <View style={styles.metaRow}>
                      <MapPin size={16} color={theme.gold} />
                      <Text style={[styles.metaText, { color: theme.text }]}>{location}</Text>
                    </View>
                  ) : null}
                  {event.address ? (
                    <Text style={[styles.address, { color: theme.mutedForeground }]}>
                      {event.address}
                    </Text>
                  ) : null}
                </View>

                <View style={styles.priceRow}>
                  <Text style={[styles.price, { color: theme.gold }]}>
                    {formatPrice(event.min_price, event.currency, t)}
                  </Text>
                </View>

                {isPast ? (
                  <EndedNotice theme={theme} t={t} />
                ) : event.ticket_url ? (
                  <Button
                    variant="gold"
                    fullWidth
                    style={styles.ticketBtn}
                    leadingIcon={<Ticket size={18} color={theme.goldForeground} />}
                    onPress={() =>
                      openExternal(event.ticket_url as string, {
                        kind: 'event',
                        source: event.source,
                        id: event.id,
                        title,
                        city: event.city_id,
                        locale,
                      })
                    }>
                    {t('events.getTickets')}
                  </Button>
                ) : null}
                {isPast ? null : <EventHotels event={event} />}

                {/* A recurring show lists every date with its own ticket
                    link; the CTA above books the next one. */}
                {dates.length > 1 ? (
                  <>
                    <View style={[styles.divider, { backgroundColor: theme.border }]} />
                    <ShowDates event={event} dates={dates} />
                  </>
                ) : null}

                {descriptionText ? (
                  <>
                    <View style={[styles.divider, { backgroundColor: theme.border }]} />
                    <Text
                      style={[
                        styles.sectionHeading,
                        { color: theme.text, fontFamily: Fonts?.serif },
                      ]}>
                      {t('events.aboutEvent')}
                    </Text>
                    <Text style={[styles.description, { color: theme.textSecondary }]}>
                      {descriptionText}
                    </Text>
                  </>
                ) : null}

                {event.source === 'awin_fnac' ? (
                  <Text style={[styles.attribution, { color: theme.mutedForeground }]}>
                    {t('events.attributionFnac')}
                  </Text>
                ) : null}
              </View>
            </View>
          </View>
          {related.length > 0 ? (
            <RelatedList heading={relatedHeading} seeAllHref={seeAllHref}>
              {related.map((e) => (
                <EventCard key={e.id} event={e} eager={false} />
              ))}
            </RelatedList>
          ) : null}
          <CityCrossSell cityId={event.city_id} />
          </>
        ) : loading ? (
          <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
            {t('events.loadingOne')}
          </Text>
        ) : error ? (
          <Text style={[styles.statusText, { color: theme.destructive }]}>
            {t('events.loadError', { error })}
          </Text>
        ) : (
          <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
            {t('events.notFound')}
          </Text>
        )}
        <SiteFooter />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  statusText: {
    fontSize: 14,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.four,
  },
  centered: {
    alignItems: 'center',
    width: '100%',
  },
  page: {
    width: '100%',
    maxWidth: MaxContentWidth,
  },
  pageLg: {
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.four,
  },
  heroBox: {
    width: '100%',
    aspectRatio: 16 / 9,
    position: 'relative',
  },
  heroBoxLg: {
    borderRadius: Radius['2xl'],
    overflow: 'hidden',
  },
  hero: {
    width: '100%',
    height: '100%',
  },
  heroScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '40%',
  },
  backBtn: {
    position: 'absolute',
    top: Spacing.three,
    left: Spacing.three,
    width: 38,
    height: 38,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroBadge: {
    position: 'absolute',
    top: Spacing.three,
    right: Spacing.three,
  },
  heartBtn: {
    position: 'absolute',
    bottom: Spacing.three,
    right: Spacing.three,
    width: 40,
    height: 40,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    marginTop: -Spacing.four,
    borderTopLeftRadius: Radius['2xl'],
    borderTopRightRadius: Radius['2xl'],
    borderWidth: 1,
    borderBottomWidth: 0,
    paddingHorizontal: Spacing.three,
    paddingTop: Spacing.four,
    paddingBottom: Spacing.five,
  },
  cardLg: {
    marginTop: -Spacing.five,
    borderRadius: Radius['2xl'],
    borderBottomWidth: 1,
    paddingHorizontal: Spacing.five,
    paddingVertical: Spacing.five,
    maxWidth: 760,
    alignSelf: 'center',
    width: '100%',
  },
  title: {
    fontSize: 26,
    fontWeight: '700',
    lineHeight: 33,
  },
  titleLg: {
    fontSize: 34,
    lineHeight: 41,
  },
  metaList: {
    marginTop: Spacing.three,
    gap: Spacing.two,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
  },
  metaText: {
    fontSize: 15,
    flex: 1,
  },
  address: {
    fontSize: 13,
    marginLeft: 24,
  },
  priceRow: {
    marginTop: Spacing.three,
  },
  price: {
    fontSize: 20,
    fontWeight: '700',
  },
  ticketBtn: {
    marginTop: Spacing.three,
  },
  endedNotice: {
    marginTop: Spacing.three,
    borderRadius: Radius.lg,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    gap: 4,
  },
  endedBanner: {
    fontSize: 15,
    fontWeight: '700',
  },
  endedHint: {
    fontSize: 13,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: Spacing.four,
  },
  sectionHeading: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: Spacing.two,
  },
  description: {
    fontSize: 15,
    lineHeight: 24,
  },
  attribution: {
    fontSize: 12,
    marginTop: Spacing.four,
  },
});
